import type { UpstreamModelCostRecord } from '@nadoutong/contracts';
import { useQueries } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';
import { listUpstreamCapabilities } from './upstreams.ts';

/**
 * Procurement cost multipliers resolved to the identity the selector
 * actually bills on (epic #1327 PR-8).
 *
 * Since #1329 the cost row is keyed `(upstream_id, upstream_channel_id,
 * capability.model)` — the *physical* model the channel buys. A routing
 * target only carries `upstream_model_name` (the capability's wire name),
 * which is not that key: a cross-model pin buys `cap.model` while
 * advertising the SKU's own catalog model. So the marketplace list joins
 * the two upstream-owned reads it can already make — capabilities
 * (capability_id → physical model + channel) and model costs
 * ((channel, model) → multiplier) — and looks a target up by its pin.
 *
 * The fan-out is per *upstream*, not per SKU: a handful of requests for
 * the whole catalog, versus one per row.
 */

const UPSTREAMS_BASE_PATH = '/v1/ops/upstreams';

export async function listUpstreamModelCosts(
  upstreamId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<readonly UpstreamModelCostRecord[]> {
  const response = await opsApiRequest<{ costs: readonly UpstreamModelCostRecord[] }>(
    `${UPSTREAMS_BASE_PATH}/${encodeURIComponent(upstreamId)}/model-costs`,
    { signal },
    fetchImpl,
  );
  return response.costs;
}

/**
 * One key per upstream. The slice caches capabilities *and* model costs
 * together because a price is only meaningful once joined to the model it
 * prices, so the name covers the pair rather than either read. It stays
 * under the `['upstreams', id, …]` prefix so the existing upstream
 * invalidations reach it.
 */
export function upstreamProcurementCostIndexQueryKey(
  upstreamId: string,
): readonly ['upstreams', string, 'procurement-cost-index'] {
  return ['upstreams', upstreamId, 'procurement-cost-index'] as const;
}

/** The subset of a routing target this lookup needs to resolve a price. */
export interface ProcurementTargetPin {
  readonly upstream_id: string;
  readonly upstream_channel_id: string;
  readonly capability_id: string | null;
  /** The SKU's own catalog model — the fallback for legacy unpinned rows. */
  readonly model: string;
  readonly enabled: boolean;
}

export interface ProcurementCostIndex {
  /**
   * Resolve one bound target to its procurement multiplier. Null means
   * "no explicit price for the identity this target buys" — never an
   * implicit 1x, matching the gateway's exact-snapshot semantics.
   *
   * Null is only meaningful for a target whose upstream resolved; ask
   * `isUpstreamResolved` first, or a failed read reads as a missing price.
   */
  readonly multiplierForTarget: (target: ProcurementTargetPin) => number | null;
  /**
   * False when this upstream's slice has not landed — still loading, or
   * failed. Callers must treat its targets as unknown rather than unpriced:
   * an absent answer is not evidence of absence.
   */
  readonly isUpstreamResolved: (upstreamId: string) => boolean;
  readonly isPending: boolean;
}

interface UpstreamCostSlice {
  readonly upstreamId: string;
  /** capability_id → the physical model that capability buys. */
  readonly modelByCapabilityId: ReadonlyMap<string, string>;
  /** `${channelId}\u0000${model}` → multiplier. */
  readonly multiplierByChannelModel: ReadonlyMap<string, number>;
}

function channelModelKey(channelId: string, model: string): string {
  return `${channelId}\u0000${model}`;
}

async function loadUpstreamCostSlice(
  upstreamId: string,
  fetchImpl: typeof fetch,
  signal: AbortSignal,
): Promise<UpstreamCostSlice> {
  const [capabilities, costs] = await Promise.all([
    listUpstreamCapabilities(upstreamId, fetchImpl, signal),
    listUpstreamModelCosts(upstreamId, fetchImpl, signal),
  ]);
  return {
    upstreamId,
    modelByCapabilityId: new Map(capabilities.map((capability) => [capability.id, capability.model])),
    multiplierByChannelModel: new Map(
      costs.map((cost) => [channelModelKey(cost.upstream_channel_id, cost.model), cost.cost_multiplier]),
    ),
  };
}

function combineCostSlices(
  results: readonly UseQueryResult<UpstreamCostSlice, Error>[],
): ProcurementCostIndex {
  const byUpstream = new Map<string, UpstreamCostSlice>();
  for (const result of results) {
    if (result.data !== undefined) byUpstream.set(result.data.upstreamId, result.data);
  }
  return {
    multiplierForTarget: (target) => {
      const slice = byUpstream.get(target.upstream_id);
      if (slice === undefined) return null;
      // A pinned target buys its capability's physical model; a legacy
      // unpinned row can only mean the SKU's own catalog model.
      const model = target.capability_id === null
        ? target.model
        : slice.modelByCapabilityId.get(target.capability_id);
      if (model === undefined) return null;
      return slice.multiplierByChannelModel.get(
        channelModelKey(target.upstream_channel_id, model),
      ) ?? null;
    },
    // Failure needs no separate flag: a failed slice is simply absent from
    // the map, which is exactly what `isUpstreamResolved` reports — and it
    // reports it per upstream, so one bad slice cannot blank a SKU whose
    // own supply resolved fine.
    isUpstreamResolved: (upstreamId) => byUpstream.has(upstreamId),
    isPending: results.some((result) => result.isPending),
  };
}

export function useProcurementCostIndex(upstreamIds: readonly string[]): ProcurementCostIndex {
  const { fetchImpl } = useSession();
  return useQueries({
    queries: upstreamIds.map((upstreamId) => ({
      queryKey: upstreamProcurementCostIndexQueryKey(upstreamId),
      queryFn: ({ signal }: { signal: AbortSignal }): Promise<UpstreamCostSlice> =>
        loadUpstreamCostSlice(upstreamId, fetchImpl, signal),
    })),
    combine: combineCostSlices,
  });
}

/** Min/max procurement multiplier across a SKU's enabled bound targets. */
export interface ProcurementRange {
  readonly min: number;
  readonly max: number;
}

/**
 * Three-state answer, because "we could not read this" and "there is no
 * price" are different facts that a single nullable number conflates.
 *
 * `unresolved` wins over a partial range on purpose: if any bound
 * target's upstream slice is missing, the surviving legs are a subset of
 * unknown size, and rendering their min–max would quietly hide the more
 * expensive leg an operator is pricing against.
 */
export type ProcurementRangeResult =
  | { readonly kind: 'range'; readonly range: ProcurementRange }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unresolved' };

export function procurementRangeForTargets(
  targets: readonly ProcurementTargetPin[] | undefined,
  index: ProcurementCostIndex,
): ProcurementRangeResult {
  if (targets === undefined) return { kind: 'unresolved' };
  const multipliers: number[] = [];
  for (const target of targets) {
    if (!target.enabled) continue;
    if (!index.isUpstreamResolved(target.upstream_id)) return { kind: 'unresolved' };
    const multiplier = index.multiplierForTarget(target);
    if (multiplier !== null) multipliers.push(multiplier);
  }
  if (multipliers.length === 0) return { kind: 'absent' };
  return { kind: 'range', range: { min: Math.min(...multipliers), max: Math.max(...multipliers) } };
}
